import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { parseApiError } from "@/lib/error-handling";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import { useListPageParam } from "@/lib/useListPageParam";
import { useListSearchParam } from "@/lib/useListSearchParam";
import { useToast } from "@/lib/ui";
import {
  useAdminUsersList,
  useAdminUserCreate,
  useAdminUserUpdate,
  useAdminUserResetPassword,
  type AdminUserListItem,
} from "@/lib/admin-api";

const USERS_PAGE_SIZE = 10;

export function UsersList() {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useListSearchParam("q");
  const [pageFromUrl, setPageFromUrl] = useListPageParam();

  const {
    lazyParams,
    setLazyParams,
    onPage: onPageBase,
    onSort,
    resetToFirstPage: resetToFirstPageBase,
  } = useLazyTableParams({
    initialRows: USERS_PAGE_SIZE,
    initialSortField: "email",
    initialSortOrder: 1,
    initialFirst: (Math.max(1, pageFromUrl) - 1) * USERS_PAGE_SIZE,
  });

  const onPage = useCallback(
    (event: { first: number; rows: number; page?: number }) => {
      onPageBase(event);
      const newPage = (event.page ?? Math.floor(event.first / event.rows)) + 1;
      setPageFromUrl(newPage);
    },
    [onPageBase, setPageFromUrl],
  );

  const resetToFirstPage = useCallback(() => {
    resetToFirstPageBase();
    setPageFromUrl(1);
  }, [resetToFirstPageBase, setPageFromUrl]);

  useEffect(() => {
    setLazyParams((prev) => {
      const expectedFirst = (Math.max(1, pageFromUrl) - 1) * prev.rows;
      if (prev.first === expectedFirst) return prev;
      return {
        ...prev,
        first: expectedFirst,
        page: Math.max(0, pageFromUrl - 1),
      };
    });
  }, [pageFromUrl, setLazyParams]);

  const [userDialog, setUserDialog] = useState(false);
  const [user, setUser] = useState<Partial<AdminUserListItem>>({
    firstName: "",
    lastName: "",
    email: "",
    isAdmin: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const { toastRef, showToast } = useToast();

  const usersQuery = useAdminUsersList({
    page: lazyParams.page + 1,
    pageSize: lazyParams.rows,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filterText: globalFilter,
  });
  const users = (usersQuery.data as any)?.data ?? [];
  const totalRecords = (usersQuery.data as any)?.pagination?.total ?? 0;
  const loading = usersQuery.isFetching;

  const userCreate = useAdminUserCreate();
  const userUpdate = useAdminUserUpdate();
  const resetPassword = useAdminUserResetPassword();

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    resetToFirstPage();
  };

  const openNew = () => {
    setUser({
      firstName: "",
      lastName: "",
      email: "",
      isAdmin: false,
    });
    setSubmitted(false);
    setUserDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setUserDialog(false);
  };

  const saveUser = async () => {
    setSubmitted(true);

    if (user.firstName?.trim() && user.lastName?.trim() && user.email?.trim()) {
      try {
        if (user.id) {
          await userUpdate.mutateAsync({
            params: { path: { id: user.id } },
            body: {
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              isAdmin: user.isAdmin ?? false,
            },
          });
          showToast({
            severity: "success",
            summary: "Successful",
            detail: "User Updated",
            life: 3000,
          });
        } else {
          await userCreate.mutateAsync({
            body: {
              firstName: user.firstName!,
              lastName: user.lastName!,
              email: user.email!,
              isAdmin: user.isAdmin ?? false,
            },
          });
          showToast({
            severity: "success",
            summary: "Successful",
            detail: `User created. Password reset email sent to ${user.email}`,
            life: 5000,
          });
        }

        setUserDialog(false);
        setUser({
          firstName: "",
          lastName: "",
          email: "",
          isAdmin: false,
        });
      } catch (error: unknown) {
        const { message } = parseApiError(error);
        const detail = message || "Failed to save user";
        showToast({ severity: "error", summary: "Error", detail, life: 3000 });
      }
    }
  };

  const editUser = (user: AdminUserListItem) => {
    setUser({ ...user });
    setUserDialog(true);
  };

  const handleResetPassword = async (user: AdminUserListItem) => {
    if (!user.email) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "User does not have an email address",
        life: 3000,
      });
      return;
    }

    try {
      await resetPassword.mutateAsync({
        params: { path: { id: user.id } },
      });
      showToast({
        severity: "success",
        summary: "Successful",
        detail: `Password reset email sent to ${user.email}`,
        life: 5000,
      });
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      const detail = message || "Failed to reset password";
      showToast({ severity: "error", summary: "Error", detail, life: 3000 });
    }
  };

  const nameBodyTemplate = (rowData: AdminUserListItem) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

  const emailBodyTemplate = (rowData: AdminUserListItem) => {
    return rowData.email || "-";
  };

  const adminBodyTemplate = (rowData: AdminUserListItem) => {
    return rowData.isAdmin ? (
      <Tag value="Yes" severity="success" />
    ) : (
      <Tag value="No" severity="info" />
    );
  };

  const actionBodyTemplate = (rowData: AdminUserListItem) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => navigate(`/users/${rowData.id}`)}
          tooltip="View"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => editUser(rowData)}
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-key"
          className="p-button-text p-button-sm"
          onClick={() => {
            void handleResetPassword(rowData);
          }}
          tooltip="Reset Password"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const userDialogFooter = (
    <>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          void saveUser();
        }}
      />
    </>
  );

  return (
    <div className="card">
      <Toast ref={toastRef} />

      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold">Manage Users</h1>
        <div className="flex gap-2 flex-column md:flex-row">
          <span className="p-input-icon-left w-full md:w-auto">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={onFilter}
              placeholder="Search by name or email..."
              className="w-full"
            />
          </span>
          <Button
            label="New User"
            icon="pi pi-plus"
            onClick={openNew}
            className="w-full md:w-auto p-button-outlined"
          />
        </div>
      </div>

      <DataTable
        value={users}
        lazy
        paginator
        rows={lazyParams.rows}
        first={lazyParams.first}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        loading={loading}
        responsiveLayout="scroll"
        emptyMessage="No users found"
        className="text-sm"
      >
        <Column
          field="firstName"
          header="Name"
          sortable
          body={nameBodyTemplate}
          style={{ minWidth: "200px" }}
        />
        <Column
          field="email"
          header="Email"
          sortable
          body={emailBodyTemplate}
          style={{ minWidth: "250px" }}
        />
        <Column
          field="isAdmin"
          header="Admin"
          sortable
          body={adminBodyTemplate}
          style={{ minWidth: "100px" }}
        />
        <Column
          body={actionBodyTemplate}
          header="Actions"
          style={{ minWidth: "150px" }}
        />
      </DataTable>

      <Dialog
        visible={userDialog}
        style={{ width: "450px" }}
        header={user.id ? "Edit User" : "New User"}
        modal
        className="p-fluid"
        footer={userDialogFooter}
        onHide={hideDialog}
      >
        <div className="field">
          <label htmlFor="firstName">First Name</label>
          <InputText
            id="firstName"
            value={user.firstName}
            onChange={(e) => setUser({ ...user, firstName: e.target.value })}
            required
            autoFocus
            className={submitted && !user.firstName ? "p-invalid" : ""}
          />
          {submitted && !user.firstName && (
            <small className="p-error">First name is required.</small>
          )}
        </div>
        <div className="field">
          <label htmlFor="lastName">Last Name</label>
          <InputText
            id="lastName"
            value={user.lastName}
            onChange={(e) => setUser({ ...user, lastName: e.target.value })}
            required
            className={submitted && !user.lastName ? "p-invalid" : ""}
          />
          {submitted && !user.lastName && (
            <small className="p-error">Last name is required.</small>
          )}
        </div>
        <div className="field">
          <label htmlFor="email">Email</label>
          <InputText
            id="email"
            type="email"
            value={user.email}
            onChange={(e) => setUser({ ...user, email: e.target.value })}
            required
            className={submitted && !user.email ? "p-invalid" : ""}
          />
          {submitted && !user.email && (
            <small className="p-error">Email is required.</small>
          )}
          {!user.id && (
            <small className="p-text-secondary">
              A password reset email will be sent to this address.
            </small>
          )}
        </div>
        <div className="field-checkbox">
          <input
            type="checkbox"
            id="isAdmin"
            checked={user.isAdmin ?? false}
            onChange={(e) => setUser({ ...user, isAdmin: e.target.checked })}
          />
          <label htmlFor="isAdmin">Admin</label>
        </div>
      </Dialog>
    </div>
  );
}

